import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, FileText } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { classifyError } from '@/lib/errors';
import { useFinalidades, useTemplates } from '@/hooks/useTemplates';
import type { TemplateFinalidadeResponse } from '@/api/templates';
import { BindingDialog } from './BindingDialog';

type DomainTab = 'email' | 'ia';

const EMAIL_CATEGORIAS = ['EMAIL', 'EMAIL_LAYOUT'];
const IA_CATEGORIAS = ['PROMPT', 'PROMPT_SYSTEM'];

function categoriaLabel(cat: string): string {
  switch (cat) {
    case 'EMAIL': return 'Email';
    case 'EMAIL_LAYOUT': return 'Layout';
    case 'PROMPT': return 'Prompt';
    case 'PROMPT_SYSTEM': return 'Sistema';
    default: return cat;
  }
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const domain = (searchParams.get('tab') === 'ia' ? 'ia' : 'email') as DomainTab;
  const search = searchParams.get('q') ?? '';

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v) next.set(k, v); else next.delete(k);
      }
      return next;
    }, { replace: true });
  };
  const setDomain = (d: DomainTab) => updateParams({ tab: d === 'email' ? null : d });
  const setSearch = (q: string) => updateParams({ q: q || null });
  const [bindingDialogOpen, setBindingDialogOpen] = useState(false);
  const [selectedFinalidade, setSelectedFinalidade] = useState<TemplateFinalidadeResponse | null>(null);

  const { data: allTemplates, isLoading: loadingT, error: errorT } = useTemplates();
  const { data: allFinalidades, isLoading: loadingF, error: errorF } = useFinalidades();

  const isLoading = loadingT || loadingF;
  const error = errorT || errorF;

  // Mapa finalidade_nome → dados da finalidade (para abrir dialog)
  const finalidadeMap = useMemo(() => {
    const map = new Map<string, TemplateFinalidadeResponse>();
    allFinalidades?.forEach((f) => map.set(f.nome, f));
    return map;
  }, [allFinalidades]);

  // Mapa finalidade_nome → versão em produção
  const bindingMap = useMemo(() => {
    const map = new Map<string, number>();
    allFinalidades?.forEach((f) => {
      if (f.binding_atual) {
        map.set(f.nome, f.binding_atual.versao_numero);
      }
    });
    return map;
  }, [allFinalidades]);

  // Templates filtrados por domínio + busca, com info de binding
  const filtered = useMemo(() => {
    const domainCats = domain === 'email' ? EMAIL_CATEGORIAS : IA_CATEGORIAS;
    return (allTemplates ?? [])
      .filter((t) => {
        if (!domainCats.includes(t.categoria)) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return t.nome.toLowerCase().includes(q) || (t.finalidade_id?.toLowerCase().includes(q));
      })
      .map((t) => ({
        ...t,
        binding_versao: t.finalidade_id ? (bindingMap.get(t.finalidade_id) ?? null) : null,
      }));
  }, [allTemplates, bindingMap, domain, search]);

  const countByDomain = (d: DomainTab) => {
    const cats = d === 'email' ? EMAIL_CATEGORIAS : IA_CATEGORIAS;
    return (allTemplates ?? []).filter((t) => cats.includes(t.categoria)).length;
  };

  const handleOpenBinding = (finalidadeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Não navegar para o editor
    const finalidade = finalidadeMap.get(finalidadeId);
    if (!finalidade) return;
    setSelectedFinalidade(finalidade);
    setBindingDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        subtitle="Gerencie templates de email e prompts de IA."
        icon={<FileText />}
      />

      {/* Filtros — soltos, sem Card */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nome ou finalidade..."
          className="w-full sm:max-w-sm"
        />

        <div className="flex items-center gap-2">
          <Tabs value={domain} onValueChange={(v) => setDomain(v as DomainTab)}>
            <TabsList>
              <TabsTrigger value="email">Email ({countByDomain('email')})</TabsTrigger>
              <TabsTrigger value="ia">IA ({countByDomain('ia')})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Dados */}
      {isLoading && <Spinner size={32} label="Carregando templates" />}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}
      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          icon={<FileText />}
          title="Nenhum template encontrado"
          description="Tente ajustar a busca."
        />
      )}
      {filtered.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Finalidade</TableHead>
                  <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell text-center">Versão</TableHead>
                  <TableHead className="text-center">Produção</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => navigate(`/app/admin/templates/${t.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex flex-col gap-0.5">
                        <span>{t.nome}</span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                          <Badge variant="outline" className="text-[11px] px-1.5 py-0">{categoriaLabel(t.categoria)}</Badge>
                          {t.finalidade_id && <span>{t.finalidade_id}</span>}
                          {t.versao_atual != null && <span>v{t.versao_atual}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                      {t.finalidade_id ?? '—'}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline">{categoriaLabel(t.categoria)}</Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-center text-sm">
                      {t.versao_atual != null ? `v${t.versao_atual}` : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <BindingBadge
                        finalidadeId={t.finalidade_id}
                        versaoAtual={t.versao_atual}
                        bindingVersao={t.binding_versao}
                      />
                    </TableCell>
                    <TableCell>
                      {t.finalidade_id && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={(e) => handleOpenBinding(t.finalidade_id!, e)}
                            >
                              <ArrowRightLeft className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Alterar vínculo de produção</TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog de vinculação — key força remount ao trocar finalidade */}
      <BindingDialog
        key={selectedFinalidade?.nome ?? ''}
        open={bindingDialogOpen}
        onOpenChange={setBindingDialogOpen}
        finalidade={selectedFinalidade}
        templates={allTemplates ?? []}
      />
    </div>
  );
}

function BindingBadge({ finalidadeId, versaoAtual, bindingVersao }: {
  finalidadeId: string | null;
  versaoAtual: number | null;
  bindingVersao: number | null;
}) {
  if (!finalidadeId) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  if (bindingVersao == null) {
    return (
      <Badge variant="outline" className="text-amber-600 border-amber-300">
        Não publicado
      </Badge>
    );
  }
  if (bindingVersao === versaoAtual) {
    return <Badge className="bg-green-600 hover:bg-green-600">v{bindingVersao}</Badge>;
  }
  return (
    <Badge variant="outline" className="text-amber-600 border-amber-300">
      v{bindingVersao}
    </Badge>
  );
}
